/**
 * A usable coordinate, or null.
 *
 * <p>Geo columns arrive as numbers, numeric strings, or the literal string 'null' (staging stores
 * unset coordinates that way), and an uncaptured point is often 0,0 — off the coast of Ghana, never a
 * polling unit. All of those are unknown, never a real place and never a distance of 0.</p>
 */
export const toCoordinate = (value: unknown): number | null => {
	if (value === null || value === undefined) return null;
	const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
	return Number.isFinite(n) ? n : null;
};

const EARTH_RADIUS_M = 6_371_000;
const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in metres, or null when either point is unknown. */
export const distanceMetres = (
	from: { latitude: unknown; longitude: unknown },
	to: { latitude: unknown; longitude: unknown }
): number | null => {
	const lat1 = toCoordinate(from.latitude);
	const lng1 = toCoordinate(from.longitude);
	const lat2 = toCoordinate(to.latitude);
	const lng2 = toCoordinate(to.longitude);
	if (lat1 === null || lng1 === null || lat2 === null || lng2 === null) return null;
	if ((lat1 === 0 && lng1 === 0) || (lat2 === 0 && lng2 === 0)) return null;
	if (Math.abs(lat1) > 90 || Math.abs(lat2) > 90 || Math.abs(lng1) > 180 || Math.abs(lng2) > 180) return null;

	const dLat = rad(lat2 - lat1);
	const dLng = rad(lng2 - lng1);
	const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
	return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
};

/** "About 240 m", "About 1.4 km" — rounded, because a phone fix is not that precise. */
export const formatDistance = (metres: number): string => {
	if (metres < 1000) return `About ${Math.max(10, Math.round(metres / 10) * 10)} m`;
	const km = metres / 1000;
	return `About ${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
};
